import merge from 'lodash/merge';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { fetchCurrentUser, currentUserShowSuccess } from '../../ducks/user.duck';

// ================ Action types ================ //

export const SAVE_CONTACT_DETAILS_REQUEST = 'app/ContactDetailsPage/SAVE_CONTACT_DETAILS_REQUEST';
export const SAVE_CONTACT_DETAILS_SUCCESS = 'app/ContactDetailsPage/SAVE_CONTACT_DETAILS_SUCCESS';
export const SAVE_EMAIL_ERROR = 'app/ContactDetailsPage/SAVE_EMAIL_ERROR';
export const SAVE_NHS_NUMBER_ERROR = 'app/ContactDetailsPage/SAVE_NHS_NUMBER_ERROR';
export const SAVE_FAVOURITES_ERROR = 'app/ContactDetailsPage/SAVE_FAVOURITES_ERROR';
export const SAVE_PHONE_NUMBER_ERROR = 'app/ContactDetailsPage/SAVE_PHONE_NUMBER_ERROR';
export const SAVE_BIRTHDAY_ERROR = 'app/ContactDetailsPage/SAVE_BIRTHDAY_ERROR';

export const SAVE_CONTACT_DETAILS_CLEAR = 'app/ContactDetailsPage/SAVE_CONTACT_DETAILS_CLEAR';

export const RESET_PASSWORD_REQUEST = 'app/ContactDetailsPage/RESET_PASSWORD_REQUEST';
export const RESET_PASSWORD_SUCCESS = 'app/ContactDetailsPage/RESET_PASSWORD_SUCCESS';
export const RESET_PASSWORD_ERROR = 'app/ContactDetailsPage/RESET_PASSWORD_ERROR';

// ================ Reducer ================ //

const initialState = {
  saveEmailError: null,
  saveFavouritesError: null,
  saveNhsNumberError: null,
  savePhoneNumberError: null,
  savBirthdayError: null,
  saveContactDetailsInProgress: false,
  contactDetailsChanged: false,
  resetPasswordInProgress: false,
  resetPasswordError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SAVE_CONTACT_DETAILS_REQUEST:
      return {
        ...state,
        saveContactDetailsInProgress: true,
        saveEmailError: null,
        saveNhsNumberError: null,
        saveFavouritesError: null,
        savePhoneNumberError: null,
        saveBirthdayError: null,
        contactDetailsChanged: false,
      };
    case SAVE_CONTACT_DETAILS_SUCCESS:
      return { ...state, saveContactDetailsInProgress: false, contactDetailsChanged: true };
    case SAVE_EMAIL_ERROR:
      return { ...state, saveContactDetailsInProgress: false, saveEmailError: payload };
    case SAVE_NHS_NUMBER_ERROR:
      return { ...state, saveContactDetailsInProgress: false, saveNhsNumberError: payload };
    case SAVE_FAVOURITES_ERROR:
      return { ...state, saveContactDetailsInProgress: false, saveFavouritesError: payload };
    case SAVE_PHONE_NUMBER_ERROR:
      return { ...state, saveContactDetailsInProgress: false, savePhoneNumberError: payload };
    case SAVE_BIRTHDAY_ERROR:
      return { ...state, saveContactDetailsInProgress: false, saveBirthdayError: payload };

    case SAVE_CONTACT_DETAILS_CLEAR:
      return {
        ...state,
        saveContactDetailsInProgress: false,
        saveEmailError: null,
        savePhoneNumberError: null,
        saveBirthdayError: null,
        contactDetailsChanged: false,
      };

    case RESET_PASSWORD_REQUEST:
      return { ...state, resetPasswordInProgress: true, resetPasswordError: null };
    case RESET_PASSWORD_SUCCESS:
      return { ...state, resetPasswordInProgress: false };
    case RESET_PASSWORD_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, resetPasswordInProgress: false, resetPasswordError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const saveContactDetailsRequest = () => ({ type: SAVE_CONTACT_DETAILS_REQUEST });
export const saveContactDetailsSuccess = () => ({ type: SAVE_CONTACT_DETAILS_SUCCESS });
export const saveEmailError = error => ({
  type: SAVE_EMAIL_ERROR,
  payload: error,
  error: true,
});
export const saveNhsNumberError = error => ({
  type: SAVE_NHS_NUMBER_ERROR,
  payload: error,
  error: true,
});
export const saveFavouritesError = error => ({
  type: SAVE_FAVOURITES_ERROR,
  payload: error,
  error: true,
});
export const savePhoneNumberError = error => ({
  type: SAVE_PHONE_NUMBER_ERROR,
  payload: error,
  error: true,
});
export const saveBirthdayError = error => ({
  type: SAVE_BIRTHDAY_ERROR,
  payload: error,
  error: true,
});

export const saveContactDetailsClear = () => ({ type: SAVE_CONTACT_DETAILS_CLEAR });

export const resetPasswordRequest = () => ({ type: RESET_PASSWORD_REQUEST });

export const resetPasswordSuccess = () => ({ type: RESET_PASSWORD_SUCCESS });

export const resetPasswordError = e => ({
  type: RESET_PASSWORD_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

/**
 * Make a phone number update request to the API and return the current user.
 */
const requestSaveNhsNumber = params => (dispatch, getState, sdk) => {
  const nhsNumber = params.nhsNumber;

  return sdk.currentUser
    .updateProfile(
      { privateData: { nhsNumber } },
      {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      }
    )
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }

      const currentUser = entities[0];
      return currentUser;
    })
    .catch(e => {
      dispatch(saveNhsNumberError(storableError(e)));
      // pass the same error so that the SAVE_CONTACT_DETAILS_SUCCESS
      // action will not be fired
      throw e;
    });
};

/**
 * Make a Favourites update request to the API and return the current user.
 */
const requestSaveFavourites = params => (dispatch, getState, sdk) => {
  const favourites = params.favourites;

  return sdk.currentUser
    .updateProfile(
      { privateData: { favourites } },
      {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      }
    )
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }

      const currentUser = entities[0];
      return currentUser;
    })
    .catch(e => {
      dispatch(saveFavouritesError(storableError(e)));
      // pass the same error so that the SAVE_CONTACT_DETAILS_SUCCESS
      // action will not be fired
      throw e;
    });
};

/**
 * Make a phone number update request to the API and return the current user.
 */
const requestSavePhoneNumber = params => (dispatch, getState, sdk) => {
  const phoneNumber = params.phoneNumber;

  return sdk.currentUser
    .updateProfile(
      { privateData: { phoneNumber } },
      {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      }
    )
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }

      const currentUser = entities[0];
      return currentUser;
    })
    .catch(e => {
      dispatch(savePhoneNumberError(storableError(e)));
      // pass the same error so that the SAVE_CONTACT_DETAILS_SUCCESS
      // action will not be fired
      throw e;
    });
};
/**
 * Make a birthday (dob) update request to the API and return the current user.
 */
const requestSaveBirthday = params => (dispatch, getState, sdk) => {
  const birthday = params.birthday;

  return sdk.currentUser
    .updateProfile(
      { privateData: { birthday } },
      {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      }
    )
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }

      const currentUser = entities[0];
      return currentUser;
    })
    .catch(e => {
      dispatch(saveBirthdayError(storableError(e)));
      // pass the same error so that the SAVE_CONTACT_DETAILS_SUCCESS
      // action will not be fired
      throw e;
    });
};

/**
 * Make a email update request to the API and return the current user.
 */
const requestSaveEmail = params => (dispatch, getState, sdk) => {
  const { email, currentPassword } = params;

  return sdk.currentUser
    .changeEmail(
      { email, currentPassword },
      {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      }
    )
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.changeEmail response');
      }

      const currentUser = entities[0];
      return currentUser;
    })
    .catch(e => {
      dispatch(saveEmailError(storableError(e)));
      // pass the same error so that the SAVE_CONTACT_DETAILS_SUCCESS
      // action will not be fired
      throw e;
    });
};

/**
 * Save email and update the current user.
 */
const saveEmail = params => (dispatch, getState, sdk) => {
  return (
    dispatch(requestSaveEmail(params))
      .then(user => {
        dispatch(currentUserShowSuccess(user));
        dispatch(saveContactDetailsSuccess());
      })
      // error action dispatched in requestSaveEmail
      .catch(e => null)
  );
};

/**
 * Save Nhs number and update the current user.
 */
const saveNhsNumber = params => (dispatch, getState, sdk) => {
  return (
    dispatch(requestSaveNhsNumber(params))
      .then(user => {
        dispatch(currentUserShowSuccess(user));
        dispatch(saveContactDetailsSuccess());
      })
      // error action dispatched in requestSaveNhsNumber
      .catch(e => null)
  );
};

/**
 * Save Favourites and update the current user.
 */
const saveFavourites = params => (dispatch, getState, sdk) => {
  return (
    dispatch(requestSaveFavourites(params))
      .then(user => {
        dispatch(currentUserShowSuccess(user));
        dispatch(saveContactDetailsSuccess());
      })
      // error action dispatched in requestSaveFavourites
      .catch(e => null)
  );
};

/**
 * Save phone number and update the current user.
 */
const savePhoneNumber = params => (dispatch, getState, sdk) => {
  return (
    dispatch(requestSavePhoneNumber(params))
      .then(user => {
        dispatch(currentUserShowSuccess(user));
        dispatch(saveContactDetailsSuccess());
      })
      // error action dispatched in requestSavePhoneNumber
      .catch(e => null)
  );
};
/**
 * Save birthday (dob) and update the current user.
 */
const saveBirthday = params => (dispatch, getState, sdk) => {
  return (
    dispatch(requestSaveBirthday(params))
      .then(user => {
        dispatch(currentUserShowSuccess(user));
        dispatch(saveContactDetailsSuccess());
      })
      // error action dispatched in requestSaveBirthday
      .catch(e => null)
  );
};

/**
 * Save email and phone number and birthday and update the current user.
 */
const saveEmailAndPhoneNumber = params => (dispatch, getState, sdk) => {
  const { email, favourites, nhsNumber, phoneNumber, birthday, currentPassword } = params;

  // order of promises: 1. email, 2. phone number, 3. birthday, 4. nhs number
  const promises = [
    dispatch(requestSaveEmail({ email, currentPassword })),
    dispatch(requestSavePhoneNumber({ phoneNumber })),
    dispatch(requestSaveBirthday({ birthday })),
    dispatch(requestSaveNhsNumber({ nhsNumber })),
    dispatch(requestSaveFavourites({ favourites })),
  ];

  return Promise.all(promises)
    .then(values => {
      // Array of two user objects is resolved
      // the first one is from the email update
      // the second one is from the phone number update
      // the third one is from the nhs number update

      const saveEmailUser = values[0];
      const savePhoneNumberUser = values[1];
      const saveBirthdayUser = values[2];
      const saveNhsNumberUser = values[3];
      const saveFavouritesUser = values[4];

      // merge the protected data from the user object returned
      // by the phone update operation
      const privateData = [
        saveFavouritesUser.attributes.profile.privateData,
        saveNhsNumberUser.attributes.profile.privateData,
        savePhoneNumberUser.attributes.profile.privateData,
        saveBirthdayUser.attributes.profile.privateData,
      ];
      const favouritesMergeSource = { attributes: { profile: { privateData } } };
      const nhsNumberMergeSource = { attributes: { profile: { privateData } } };
      const phoneNumberMergeSource = { attributes: { profile: { privateData } } };
      const birthdayMergeSource = { attributes: { profile: { privateData } } };

      const currentUser = merge(
        saveEmailUser,
        favouritesMergeSource,
        nhsNumberMergeSource,
        phoneNumberMergeSource,
        birthdayMergeSource
      );
      dispatch(currentUserShowSuccess(currentUser));
      dispatch(saveContactDetailsSuccess());
    })
    .catch(e => null);
};

/**
 * Update contact details, actions depend on which data has changed
 */
export const saveContactDetails = params => (dispatch, getState, sdk) => {
  dispatch(saveContactDetailsRequest());

  const {
    email,
    currentEmail,
    favourites,
    nhsNumber,
    phoneNumber,
    currentFavourites,
    currentNhsNumber,
    currentPhoneNumber,
    currentPassword,
    birthday,
    currentBirthday,
  } = params;

  const emailChanged = email !== currentEmail;
  const favouritesChanged = favourites !== currentFavourites;
  const nhsNumberChanged = nhsNumber !== currentNhsNumber;
  const phoneNumberChanged = phoneNumber !== currentPhoneNumber;
  const birthdayChanged = birthday !== currentBirthday;

  if (
    emailChanged &&
    favouritesChanged &&
    nhsNumberChanged &&
    phoneNumberChanged &&
    birthdayChanged
  ) {
    return dispatch(
      saveEmailAndPhoneNumber({
        email,
        currentPassword,
        favourites,
        nhsNumber,
        phoneNumber,
        birthday,
      })
    );
  } else if (emailChanged) {
    return dispatch(saveEmail({ email, currentPassword }));
  } else if (favouritesChanged) {
    return dispatch(saveFavourites({ favourites }));
  } else if (nhsNumberChanged) {
    return dispatch(saveNhsNumber({ nhsNumber }));
  } else if (phoneNumberChanged) {
    return dispatch(savePhoneNumber({ phoneNumber }));
  } else if (birthdayChanged) {
    return dispatch(saveBirthday({ birthday }));
  }
};

export const resetPassword = email => (dispatch, getState, sdk) => {
  dispatch(resetPasswordRequest());
  return sdk.passwordReset
    .request({ email })
    .then(() => dispatch(resetPasswordSuccess()))
    .catch(e => dispatch(resetPasswordError(storableError(e))));
};

export const loadData = () => {
  // Since verify email happens in separate tab, current user's data might be updated
  return fetchCurrentUser();
};
